import {
  OrganizationAccess,
  OrganizationAccessDetails,
} from '@easy-genomics/shared-lib/src/app/types/easy-genomics/user';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type NameOptions = {
  firstName: string | null;
  preferredName: string | null;
  lastName: string | null;
  email: string | null;
};

function displayName(nameOptions: NameOptions): string {
  const { firstName, preferredName, lastName, email } = nameOptions;
  const preferredOrFirstName = preferredName || firstName;

  if (preferredOrFirstName && lastName) return `${preferredOrFirstName} ${lastName}`;
  if (preferredOrFirstName) return preferredOrFirstName;
  if (lastName) return lastName;
  if (email) return email;
  return '???';
}

function initials(nameOptions: NameOptions, superuser: boolean = false): string {
  if (superuser) return '#';

  const { preferredName, firstName, lastName, email } = nameOptions;
  const preferredOrFirstName = preferredName || firstName;

  const firstInitial = preferredOrFirstName?.charAt(0);
  const lastInitial = lastName?.charAt(0);
  const emailInitial = email?.charAt(0);

  if (firstInitial && lastInitial) return firstInitial + lastInitial;
  if (firstInitial) return firstInitial;
  if (lastInitial) return lastInitial;
  if (emailInitial) return emailInitial;
  return '??';
}

interface UserStoreState {
  currentOrg: {
    OrganizationId: string | null;
  };
  mostRecentLab: {
    LaboratoryId: string | null;
  };
  currentUserPermissions: {
    isSuperuser: boolean | null;
    orgPermissions: OrganizationAccess | null;
  };
  currentUserDetails: {
    id: string | null;
    firstName: string | null;
    lastName: string | null;
    preferredName: string | null;
    email: string | null;
  };

  currentOrgId: () => string | null;
  mostRecentLabId: () => string | null;

  isSuperuser: () => boolean;

  isOrgAdminForOrg: (orgId: string) => boolean;
  isLabMemberForOrg: (orgId: string, labId: string) => boolean;
  isLabManagerForOrg: (orgId: string, labId: string) => boolean;

  canManageAnyOrgs: () => boolean;
  canManageOrg: (orgId: string) => boolean;
  canCreateLabForOrg: (orgId: string) => boolean;
  canViewLabForOrg: (orgId: string, labId: string) => boolean;
  canEditLabUsersForOrg: (orgId: string, labId: string) => boolean;
  canEditLabDetailsForOrg: (orgId: string, labId: string) => boolean;
  canDeleteLabForOrg: (orgId: string) => boolean;
  canAddLabUsersForOrg: (orgId: string, labId: string) => boolean;

  isOrgAdmin: () => boolean;
  isLabMember: (labId: string) => boolean;
  isLabManager: (labId: string) => boolean;
  canCreateLab: () => boolean;
  canViewLab: (labId: string) => boolean;
  canEditLabUsers: (labId: string) => boolean;
  canEditLabDetails: (labId: string) => boolean;
  canDeleteLab: () => boolean;
  canAddLabUsers: (labId: string) => boolean;

  currentUserDisplayName: () => string;
  currentUserInitials: () => string;

  setOrgAccess: (val: { OrganizationId: string | null }) => void;
  reset: () => void;
}

const initialState = {
  currentOrg: { OrganizationId: null as string | null },
  mostRecentLab: { LaboratoryId: null as string | null },
  currentUserPermissions: {
    isSuperuser: null as boolean | null,
    orgPermissions: null as OrganizationAccess | null,
  },
  currentUserDetails: {
    id: null as string | null,
    firstName: null as string | null,
    lastName: null as string | null,
    preferredName: null as string | null,
    email: null as string | null,
  },
};

export const useUserStore = create<UserStoreState>()(
  persist(
    (set, get) => ({
      ...initialState,

      currentOrgId: () => get().currentOrg.OrganizationId,
      mostRecentLabId: () => get().mostRecentLab.LaboratoryId,

      isSuperuser: () => !!get().currentUserPermissions.isSuperuser,

      isOrgAdminForOrg: (orgId: string) =>
        get().isSuperuser() || !!get().currentUserPermissions.orgPermissions?.[orgId]?.OrganizationAdmin,

      isLabMemberForOrg: (orgId: string, labId: string) =>
        !!get().currentUserPermissions.orgPermissions?.[orgId]?.LaboratoryAccess?.[labId],

      isLabManagerForOrg: (orgId: string, labId: string) =>
        !!get().currentUserPermissions.orgPermissions?.[orgId]?.LaboratoryAccess?.[labId]?.LabManager,

      canManageAnyOrgs: () =>
        !!get().currentUserPermissions.isSuperuser ||
        Object.values(get().currentUserPermissions.orgPermissions ?? {}).some(
          (perms: OrganizationAccessDetails) => !!perms.OrganizationAdmin,
        ),

      canManageOrg: (orgId: string) => !!get().currentUserPermissions.isSuperuser || get().isOrgAdminForOrg(orgId),

      canCreateLabForOrg: (orgId: string) => !get().isSuperuser() && get().isOrgAdminForOrg(orgId),

      canViewLabForOrg: (orgId: string, labId: string) =>
        get().isOrgAdminForOrg(orgId) || get().isLabMemberForOrg(orgId, labId),

      canEditLabUsersForOrg: (orgId: string, labId: string) =>
        get().isOrgAdminForOrg(orgId) || get().isLabManagerForOrg(orgId, labId),

      canEditLabDetailsForOrg: (orgId: string, labId: string) =>
        get().isOrgAdminForOrg(orgId) || get().isLabManagerForOrg(orgId, labId),

      canDeleteLabForOrg: (orgId: string) => get().isOrgAdminForOrg(orgId),

      canAddLabUsersForOrg: (orgId: string, labId: string) =>
        get().isOrgAdminForOrg(orgId) || get().isLabManagerForOrg(orgId, labId),

      isOrgAdmin: () => get().isOrgAdminForOrg(get().currentOrg.OrganizationId!),

      isLabMember: (labId: string) => get().isLabMemberForOrg(get().currentOrg.OrganizationId!, labId),

      isLabManager: (labId: string) => get().isLabManagerForOrg(get().currentOrg.OrganizationId!, labId),

      canCreateLab: () => get().canCreateLabForOrg(get().currentOrg.OrganizationId!),

      canViewLab: (labId: string) => get().canViewLabForOrg(get().currentOrg.OrganizationId!, labId),

      canEditLabUsers: (labId: string) => get().canEditLabUsersForOrg(get().currentOrg.OrganizationId!, labId),

      canEditLabDetails: (labId: string) => get().canEditLabDetailsForOrg(get().currentOrg.OrganizationId!, labId),

      canDeleteLab: () => get().canDeleteLabForOrg(get().currentOrg.OrganizationId!),

      canAddLabUsers: (labId: string) => get().canAddLabUsersForOrg(get().currentOrg.OrganizationId!, labId),

      currentUserDisplayName: () =>
        displayName({
          firstName: get().currentUserDetails.firstName,
          preferredName: get().currentUserDetails.preferredName,
          lastName: get().currentUserDetails.lastName,
          email: get().currentUserDetails.email,
        }),

      currentUserInitials: () =>
        initials(
          {
            firstName: get().currentUserDetails.firstName,
            preferredName: get().currentUserDetails.preferredName,
            lastName: get().currentUserDetails.lastName,
            email: get().currentUserDetails.email,
          },
          get().isSuperuser(),
        ),

      setOrgAccess: (val) => set({ currentOrg: val }),

      reset: () =>
        set({
          currentOrg: { OrganizationId: null },
          mostRecentLab: { LaboratoryId: null },
          currentUserPermissions: { isSuperuser: null, orgPermissions: null },
          currentUserDetails: {
            id: null,
            firstName: null,
            lastName: null,
            preferredName: null,
            email: null,
          },
        }),
    }),
    { name: 'userStore' },
  ),
);
